import { Lightning } from '@lightningjs/sdk';
import { HoverableComponent } from 'components/common/HoverableComponent';

const SPACING = 37;

export interface ActionsListTemplateSpec
  extends Lightning.Component.TemplateSpec {
  index: number;
  length: number;
  spacing: number;
  unfocusableIndices: number[];
  items: Lightning.Element.PatchTemplate[];
}

export default class ActionsList
  extends HoverableComponent<ActionsListTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<ActionsListTemplateSpec>
{
  private _index = -1;
  private _spacing = SPACING;
  private _unfocusableIndices: number[] = [];

  get index() {
    return this._index;
  }

  set index(index: ActionsListTemplateSpec['index']) {
    this._index = index;
  }

  get unfocusableIndices() {
    return this._unfocusableIndices;
  }

  set unfocusableIndices(value: number[]) {
    this._unfocusableIndices = value;
  }

  get length() {
    return this.children.length;
  }

  get spacing() {
    return this._spacing;
  }

  set spacing(spacing: ActionsListTemplateSpec['spacing']) {
    this._spacing = spacing;
  }

  get items() {
    return this.children;
  }

  set items(items: ActionsListTemplateSpec['items']) {
    this.childList.clear();
    items.forEach(item => this.addAction(item));
  }

  get isRow() {
    return this.flex.direction === 'row';
  }

  static override _template(): Lightning.Component.Template<ActionsListTemplateSpec> {
    return {
      flex: { direction: 'row' },
    };
  }

  override _inactive() {
    // sets the index to the first focusable item or -1 otherwise
    this.index = this.getNextIndex(-1) ?? -1;
  }

  override _getFocused(): Lightning.Component {
    // we use getNextIndex to account for unfocusable indices
    const validIndex = this.getNextIndex(this.index - 1) ?? -1;
    if (validIndex < 0) {
      return this;
    } else {
      return this.children[validIndex] as Lightning.Component;
    }
  }

  override _handleUp() {
    if (this.isRow) return false;
    return this.handleIndexDecrement();
  }

  override _handleDown() {
    if (this.isRow) return false;
    return this.handleIndexIncrement();
  }

  override _handleLeft() {
    if (!this.isRow) return false;
    return this.handleIndexDecrement();
  }

  override _handleRight() {
    if (!this.isRow) return false;
    return this.handleIndexIncrement();
  }

  handleIndexIncrement() {
    const nextIndex = this.getNextIndex(this.index);
    if (nextIndex !== null) {
      this.index = nextIndex;
      return true;
    }

    return false;
  }

  handleIndexDecrement() {
    const prevIndex = this.getPrevIndex(this.index);
    if (prevIndex !== null) {
      this.index = prevIndex;
      return true;
    }

    return false;
  }

  private getNextIndex(index: number) {
    let newIndex = index + 1;
    while (this.unfocusableIndices.includes(newIndex)) {
      newIndex++;
    }

    return newIndex <= this.children.length - 1 ? newIndex : null;
  }

  private getPrevIndex(index: number) {
    let newIndex = index - 1;
    while (this.unfocusableIndices.includes(newIndex)) {
      newIndex--;
    }
    return newIndex >= 0 ? newIndex : null;
  }

  addAction(action: Lightning.Element.PatchTemplate) {
    const marginNumber = this.children.length ? this.spacing : 0;

    const margin = {
      marginTop: this.isRow ? 0 : marginNumber,
      marginLeft: this.isRow ? marginNumber : 0,
    };

    this.add({
      ...action,
      flexItem: { ...margin },
    });
    this.updateIndex();
  }

  getAction(index: number) {
    return this.children[index];
  }

  removeAction(index: number) {
    this.children.splice(index, 1);
    this.updateIndex();
  }

  clearActions() {
    this.children = [];
    this.childList.clear();
    this.index = -1;
  }

  updateIndex() {
    if (this.children.length) {
      this.index = this.index === -1 ? 0 : this.index;
    } else {
      this.index = -1;
    }
  }

  getItemByRef(ref: string) {
    return this.items.find(item => item.ref === ref);
  }

  $onHover(target: unknown) {
    if (!(target instanceof Lightning.Element)) return;

    /** Because ActionsList isn't an actual CollectionWrapper and children array do not have numbered index
     *  for each individual items for HoverableListItem to leverage off CollectionWrapperWithSpeech's setItemHelper,
     *  we need to find the exact index within the children's array the current target is in, then call refocus() to
     *  let ActionsList::_getFocused() to handle the focus logic
     */
    const index = this.children.findIndex(c => c && c.id === target.id);
    if (index !== -1) {
      if (this._unfocusableIndices.includes(index)) {
        return;
      }
      this.index = index;
      this._refocus();
    }
  }
}
